import { useState, useEffect, useRef } from "preact/hooks"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Separator } from "@/components/ui/separator"
import { Badge } from "@/components/ui/badge"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  Wand2,
  Send,
  LoaderCircle,
  CheckCircle2,
  XCircle,
  History,
  ChevronRight,
  ChevronDown,
  AlertCircle,
  MessageSquare,
  FileText,
  ArrowUp,
  ArrowLeft,
} from "lucide-react"
import { format, parseISO } from "date-fns"
import {
  startBulkAnalysisStream,
  sendBulkAnalysisFollowUp,
  getBulkAnalysisHistory,
  getBulkAnalysisMessages,
  checkAlreadyAnalyzed,
  BulkAnalysisConversation,
  BulkAnalysisMessage,
  BulkDeductionResult,
  BulkAnalysisResponse,
  BulkAnalysisProgressEvent,
} from "@/api/bulk_triage"
import { DeductionListJson } from "@/api/deduction"
import { cn, format_transaction_amount } from "@/utils"
import { format_ai_confidence_percent } from "@/utils/format_ai_confidence_percent"
import { marked } from "marked"
import { setPrefilledDisputeMessage } from "@/dispute/file_dispute_drawer"
import { useLocation } from "wouter-preact"
import { ShieldAlert, ExternalLink } from "lucide-react"
import { closeBulkWizard } from "./bulk_wizard_state"
import { openDeductionDetailModal } from "./deduction_detail_modal_state"

// Configure marked for safe rendering
marked.setOptions({
  breaks: true,
  gfm: true,
})

interface BulkWizardSidebarProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedDeductions: DeductionListJson[]
  onComplete?: () => void
}

interface MessageDisplay {
  role: "user" | "assistant"
  content: string
  timestamp: string
  results?: BulkAnalysisResponse
}

// Collapsible card for each deduction result
function DeductionResultCard({ result }: { result: BulkDeductionResult }) {
  const [isOpen, setIsOpen] = useState(false)
  const [, setLocation] = useLocation()

  function handleFileDispute() {
    if (!result.dispute_message) return
    
    // Set the pre-filled message for the dispute drawer
    setPrefilledDisputeMessage(result.deduction_id, result.dispute_message)
    
    // Close the bulk wizard sidebar
    closeBulkWizard()
    
    // Navigate to the deduction detail page with dispute query param
    setLocation(`/deduction/${result.deduction_id}?open_dispute=true`)
  }

  function handleViewDeduction() {
    openDeductionDetailModal(result.deduction_id)
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger class="w-full">
        <div class={cn(
          "flex items-center justify-between p-3 rounded-lg border transition-colors cursor-pointer",
          result.is_disputable 
            ? "border-green-200 bg-green-50/50 hover:bg-green-50" 
            : "border-red-200 bg-red-50/50 hover:bg-red-50"
        )}>
          <div class="flex items-center gap-2">
            {result.is_disputable ? (
              <CheckCircle2 className="h-4 w-4 text-green-600" />
            ) : (
              <XCircle className="h-4 w-4 text-red-600" />
            )}
            <span class="font-medium text-sm">{result.invoice_number}</span>
            <Badge variant="outline" class={cn(
              "text-xs",
              result.is_disputable ? "border-green-300 text-green-700" : "border-red-300 text-red-700"
            )}>
              {result.is_disputable ? "Disputable" : "Not Disputable"}
            </Badge>
          </div>
          <div class="flex items-center gap-2">
            <span class="text-xs text-muted-foreground">
              {format_ai_confidence_percent(result.confidence)} confidence
            </span>
            {isOpen ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div class="mt-2 p-3 bg-muted/30 rounded-lg space-y-3 text-sm">
          {/* Reasoning */}
          <div>
            <div class="flex items-center gap-1 text-xs font-medium text-muted-foreground mb-1">
              <MessageSquare className="h-3 w-3" />
              Reasoning
            </div>
            <p class="text-gray-700">{result.reasoning}</p>
          </div>

          {/* Dispute Message */}
          {result.dispute_message && (
            <div>
              <div class="flex items-center gap-1 text-xs font-medium text-muted-foreground mb-1">
                <FileText className="h-3 w-3" />
                Suggested Dispute Message
              </div>
              <div class="bg-white border rounded p-2 text-gray-700 whitespace-pre-wrap">
                {result.dispute_message}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div class="flex gap-2 pt-2">
            {result.is_disputable && result.dispute_message && (
              <Button
                size="sm"
                onClick={handleFileDispute}
                class="flex-1"
              >
                <ShieldAlert class="h-3 w-3 mr-1" />
                File Dispute
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={handleViewDeduction}
              class={result.is_disputable && result.dispute_message ? "" : "flex-1"}
            >
              <ExternalLink class="h-3 w-3 mr-1" />
              View Deduction
            </Button>
          </div>

          {/* Evidence */}
          {result.evidence && result.evidence.length > 0 && (
            <div>
              <div class="text-xs font-medium text-muted-foreground mb-1">Evidence</div>
              <ul class="list-disc list-inside text-gray-700 space-y-1">
                {result.evidence.map((e: string, i: number) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </div>
          )}

          {/* PDF Analysis */}
          {result.pdf_analysis && (
            <div>
              <div class="text-xs font-medium text-muted-foreground mb-1">PDF Analysis</div>
              <p class="text-gray-700">{result.pdf_analysis}</p>
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

// Structured response display for assistant messages
function AssistantResponseDisplay({ results }: { results: BulkAnalysisResponse }) {
  return (
    <div class="space-y-4">
      {/* Summary Section */}
      <div class="bg-white border rounded-lg p-4 shadow-sm">
        <h4 class="font-semibold text-sm mb-2 flex items-center gap-2">
          <Wand2 className="h-4 w-4 text-purple-500" />
          Summary
        </h4>
        <p class="text-sm text-gray-700">{results.summary}</p>
      </div>

      {/* Cross-Deduction Findings */}
      {results.cross_deduction_findings && (
        <div class="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h4 class="font-semibold text-sm mb-2 flex items-center gap-2 text-amber-800">
            <AlertCircle className="h-4 w-4" />
            Cross-Deduction Findings
          </h4>
          <p class="text-sm text-amber-900">{results.cross_deduction_findings}</p>
        </div>
      )}

      {/* Deduction Results */}
      <div>
        <h4 class="font-semibold text-sm mb-3">Per-Deduction Analysis</h4>
        <div class="space-y-2">
          {results.deductions.map((result: BulkDeductionResult) => (
            <DeductionResultCard key={result.deduction_id} result={result} />
          ))}
        </div>
      </div>
    </div>
  )
}

// Message item component
function MessageItem({ message }: { message: MessageDisplay }) {
  const isUser = message.role === "user"

  // For assistant messages with structured results, show the structured display
  if (!isUser && message.results) {
    return (
      <div class="mb-4">
        <div class="text-xs text-muted-foreground mb-2">
          Wizard • {format(parseISO(message.timestamp), "HH:mm")}
        </div>
        <AssistantResponseDisplay results={message.results} />
      </div>
    )
  }

  // For assistant messages without structured results (follow-ups), render as markdown
  if (!isUser) {
    const html = marked.parse(message.content) as string
    return (
      <div class="mb-4">
        <div class="bg-white border rounded-lg px-4 py-3 shadow-sm">
          <div class="text-xs text-muted-foreground mb-2">
            Wizard • {format(parseISO(message.timestamp), "HH:mm")}
          </div>
          <div
            class="markdown-content text-sm text-gray-900"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        </div>
      </div>
    )
  }

  // User messages
  return (
    <div class="flex justify-end mb-4">
      <div class="max-w-[85%] bg-primary text-primary-foreground rounded-lg px-4 py-3">
        <div class="text-sm whitespace-pre-wrap">{message.content}</div>
        <div class="text-xs opacity-70 mt-1">
          {format(parseISO(message.timestamp), "HH:mm")}
        </div>
      </div>
    </div>
  )
}

export function BulkWizardSidebar({
  open,
  onOpenChange,
  selectedDeductions,
  onComplete,
}: BulkWizardSidebarProps) {
  const [mode, setMode] = useState<"analysis" | "history">(
    selectedDeductions.length > 0 ? "analysis" : "history"
  )
  const [prompt, setPrompt] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [conversation, setConversation] = useState<BulkAnalysisConversation | null>(null)
  const [messages, setMessages] = useState<MessageDisplay[]>([])
  const [history, setHistory] = useState<BulkAnalysisConversation[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [followUpInput, setFollowUpInput] = useState("")
  const [progressStatus, setProgressStatus] = useState<BulkAnalysisProgressEvent | null>(null)
  const [showWarningDialog, setShowWarningDialog] = useState(false)
  const [alreadyAnalyzedIds, setAlreadyAnalyzedIds] = useState<string[]>([])
  const [pendingAnalysis, setPendingAnalysis] = useState<(() => void) | null>(null)
  const [viewingHistoryItem, setViewingHistoryItem] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  // Update mode when sidebar opens or selected deductions change
  useEffect(() => {
    if (!open) return

    // If we have deductions selected and no active conversation, start analysis mode
    if (selectedDeductions.length > 0 && !conversation) {
      setMode("analysis")
      // Reset state for new analysis
      setMessages([])
      setError(null)
    } 
    // If we have no deductions and no active conversation, show history
    else if (selectedDeductions.length === 0 && !conversation) {
      setMode("history")
      loadHistory()
    }
    // If we have an active conversation, stay in analysis mode regardless of deductions
    // (This happens after analysis completes and deduction selection is cleared)
  }, [open, selectedDeductions.length, conversation])

  // Also load history when mode changes to history while open
  useEffect(() => {
    if (mode === "history" && open && history.length === 0) {
      loadHistory()
    }
  }, [mode, open])

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages, loading])

  async function loadHistory() {
    setHistoryLoading(true)
    setError(null)
    try {
      const conversations = await getBulkAnalysisHistory()
      console.log("Bulk analysis history loaded:", conversations)
      setHistory(conversations || [])
    } catch (err) {
      console.error("Failed to load history:", err)
      setError(err instanceof Error ? err.message : "Failed to load history")
      setHistory([])
    } finally {
      setHistoryLoading(false)
    }
  }

  async function handleStartAnalysis() {
    if (!prompt.trim() || selectedDeductions.length === 0) return

    // Check if any deductions have already been analyzed
    try {
      const checkResult = await checkAlreadyAnalyzed({
        deduction_ids: selectedDeductions.map((d: DeductionListJson) => d.id),
      })

      if (checkResult.already_analyzed.length > 0) {
        // Show warning dialog
        setAlreadyAnalyzedIds(checkResult.already_analyzed)
        setPendingAnalysis(() => () => {
          // This will be called when user confirms
          startAnalysisInternal()
        })
        setShowWarningDialog(true)
        return
      }
    } catch (err) {
      console.error("Failed to check already analyzed:", err)
      // Continue anyway if check fails
    }

    // No already analyzed deductions, proceed directly
    startAnalysisInternal()
  }

  function startAnalysisInternal() {
    if (!prompt.trim() || selectedDeductions.length === 0) return

    setLoading(true)
    setError(null)
    setProgressStatus(null)
    setShowWarningDialog(false)
    setAlreadyAnalyzedIds([])
    setPendingAnalysis(null)

    const currentPrompt = prompt.trim()
    console.log("Starting bulk analysis (streaming) with deductions:", selectedDeductions.map(d => d.id))

    // Use SSE streaming for bulk analysis
    const abortController = startBulkAnalysisStream(
      {
        deduction_ids: selectedDeductions.map((d: DeductionListJson) => d.id),
        prompt: currentPrompt,
      },
      {
        onProgress: (event: BulkAnalysisProgressEvent) => {
          console.log("Progress:", event)
          setProgressStatus(event)
        },
        onResult: (result: BulkAnalysisConversation) => {
          console.log("Bulk analysis result:", result)
          setConversation(result)
          setProgressStatus(null)

          // Build messages from the result
          const newMessages: MessageDisplay[] = [
            {
              role: "user",
              content: currentPrompt,
              timestamp: result.created_at,
            },
          ]

          if (result.results) {
            console.log("Analysis results:", result.results)
            newMessages.push({
              role: "assistant",
              content: "", // Content is in results
              timestamp: result.updated_at,
              results: result.results,
            })
          } else {
            console.warn("No results in bulk analysis response")
            newMessages.push({
              role: "assistant",
              content: "Analysis completed but no results were returned. Please try again.",
              timestamp: result.updated_at || new Date().toISOString(),
            })
          }

          setMessages(newMessages)
          setPrompt("")
          setLoading(false)

          if (onComplete) {
            onComplete()
          }
        },
        onError: (errorMessage: string) => {
          console.error("Failed to start bulk analysis:", errorMessage)
          setError(errorMessage)
          setLoading(false)
          setProgressStatus(null)
        },
      }
    )

    abortControllerRef.current = abortController
  }

  function handleConfirmWarning() {
    if (pendingAnalysis) {
      pendingAnalysis()
    }
  }

  function handleCancelWarning() {
    setShowWarningDialog(false)
    setAlreadyAnalyzedIds([])
    setPendingAnalysis(null)
  }

  // Cancel analysis if the sidebar is closed while loading
  useEffect(() => {
    if (!open && loading && abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
      setLoading(false)
      setProgressStatus(null)
    }
  }, [open, loading])

  async function handleSendFollowUp() {
    if (!followUpInput.trim() || !conversation) return

    const userMessage = followUpInput.trim()
    setFollowUpInput("")
    setLoading(true)

    // Add user message immediately
    setMessages((prev) => [
      ...prev,
      {
        role: "user",
        content: userMessage,
        timestamp: new Date().toISOString(),
      },
    ])

    try {
      const response = await sendBulkAnalysisFollowUp(conversation.id, {
        content: userMessage,
      })

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: response.content,
          timestamp: response.created_at,
        },
      ])
    } catch (err) {
      console.error("Failed to send follow-up:", err)
      setError(err instanceof Error ? err.message : "Failed to send follow-up")
    } finally {
      setLoading(false)
    }
  }

  async function handleViewHistoryItem(conv: BulkAnalysisConversation) {
    setConversation(conv)
    setMode("analysis")
    setViewingHistoryItem(true)

    try {
      const msgs = await getBulkAnalysisMessages(conv.id)
      
      // Convert to display messages, adding results to the first assistant message if available
      const displayMessages: MessageDisplay[] = msgs.map((m: BulkAnalysisMessage, index: number) => {
        const isFirstAssistant = m.role === "assistant" && 
          !msgs.slice(0, index).some((prev: BulkAnalysisMessage) => prev.role === "assistant")
        
        return {
          role: m.role,
          content: m.content,
          timestamp: m.created_at,
          // Attach results to first assistant message
          results: isFirstAssistant ? conv.results || undefined : undefined,
        }
      })
      
      setMessages(displayMessages)
    } catch (err) {
      console.error("Failed to load conversation messages:", err)
    }
  }

  function handleBackToHistory() {
    setConversation(null)
    setMode("history")
    setViewingHistoryItem(false)
    setMessages([])
    setError(null)
  }

  function handleKeyDown(e: KeyboardEvent) {
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
      e.preventDefault()
      if (conversation) {
        handleSendFollowUp()
      } else {
        handleStartAnalysis()
      }
    }
  }

  function renderAnalysisMode() {
    if (!conversation) {
      // Initial prompt input
      const footerHeight = "65px"
      
      return (
        <div class="h-full flex flex-col">
          <div 
            class="overflow-y-auto p-4"
            style={{ height: `calc(100% - ${footerHeight})` }}
          >
            <div class="mb-4">
              <h3 class="font-medium mb-2">Selected Deductions ({selectedDeductions.length})</h3>
              <div class="space-y-2 max-h-40 overflow-auto">
                {selectedDeductions.map((d: DeductionListJson) => (
                  <div key={d.id} class="text-sm p-2 bg-muted rounded-md flex justify-between">
                    <span class="font-medium">{d.invoice_number}</span>
                    <span class="text-muted-foreground">
                      {format_transaction_amount(d.kind, d.deduction_amount ?? d.invoice_amount, d.currency)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <Separator class="my-4" />

            <div class="space-y-4">
              <div>
                <label class="text-sm font-medium mb-2 block">
                  Ask the Wizard
                </label>
                <Textarea
                  ref={textareaRef}
                  placeholder="What would you like to analyze? e.g., 'Check for double dips across these free fill deductions' or 'Are these pack change fees correct?'"
                  value={prompt}
                  onInput={(e: Event) => setPrompt((e.target as HTMLTextAreaElement).value)}
                  onKeyDown={handleKeyDown}
                  rows={4}
                  class="resize-none"
                />
                <p class="text-xs text-muted-foreground mt-1">
                  Press Ctrl+Enter to send
                </p>
              </div>

              <div class="text-xs text-muted-foreground bg-muted/50 p-3 rounded-lg">
                <p class="mb-1 font-medium">The AI will analyze all selected deductions for:</p>
                <ul class="list-disc list-inside space-y-0.5">
                  <li>Double dips and duplicate charges</li>
                  <li>Pack change fee accuracy</li>
                  <li>Brand/organization validation</li>
                  <li>Cross-deduction patterns</li>
                </ul>
              </div>

              {/* Expected wait time notice */}
              <div class="text-xs text-amber-700 bg-amber-50 border border-amber-200 p-3 rounded-lg flex items-start gap-2">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>
                  <strong>Note:</strong> Analysis may take 30-60 seconds for multiple deductions with attachments. 
                  Please keep this sidebar open until the analysis completes.
                </span>
              </div>
            </div>

            {error && (
              <div class="mt-4 p-4 bg-destructive/10 text-destructive text-sm flex items-center gap-2 rounded-lg">
                <AlertCircle className="h-4 w-4" />
                {error}
              </div>
            )}

            {/* Progress indicator during streaming */}
            {loading && progressStatus && (
              <div class="mt-4 p-4 bg-purple-50 border border-purple-100 rounded-lg">
                <div class="flex items-center gap-2 text-purple-700">
                  <LoaderCircle className="h-4 w-4 animate-spin" />
                  <span class="text-sm font-medium">{progressStatus.message}</span>
                </div>
                {progressStatus.current && progressStatus.total && (
                  <div class="mt-2">
                    <div class="w-full bg-purple-200 rounded-full h-1.5">
                      <div 
                        class="bg-purple-600 h-1.5 rounded-full transition-all duration-300"
                        style={{ width: `${(progressStatus.current / progressStatus.total) * 100}%` }}
                      />
                    </div>
                    <p class="text-xs text-purple-600 mt-1">
                      Step {progressStatus.current} of {progressStatus.total}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          <div class="p-4 border-t bg-background">
            <Button
              onClick={handleStartAnalysis}
              disabled={loading || !prompt.trim()}
              class={cn(
                "w-full transition-all duration-200",
                loading && "bg-purple-600 hover:bg-purple-600"
              )}
            >
              {loading ? (
                <div class="flex items-center gap-2">
                  <LoaderCircle className="h-4 w-4 animate-spin" />
                  <span class="animate-pulse">Analyzing...</span>
                </div>
              ) : (
                <>
                  <Wand2 className="h-4 w-4 mr-2" />
                  Start Analysis
                </>
              )}
            </Button>
          </div>
        </div>
      )
    }

    // Conversation view - use calc to create scrollable area between header and footer
    const footerHeight = "85px" // approximate height of footer with textarea
    
    return (
      <div class="h-full flex flex-col">
        {/* Scrollable messages area - takes remaining height */}
        <div 
          class="overflow-y-auto p-4"
          style={{ height: `calc(100% - ${footerHeight})` }}
        >
          {messages.map((msg, idx) => (
            <MessageItem key={idx} message={msg} />
          ))}

          {loading && (
            <div class="flex justify-start mb-4">
              <div class="bg-muted rounded-lg px-4 py-2 flex items-center gap-2">
                <LoaderCircle className="h-4 w-4 animate-spin" />
                <span class="text-sm text-muted-foreground">Wizard is thinking...</span>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {error && (
          <div class="p-4 bg-destructive/10 text-destructive text-sm flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />
            {error}
          </div>
        )}

        {/* Follow-up input - fixed at bottom */}
        <div class="p-4 border-t bg-background">
          <div class="flex gap-2">
            <Textarea
              placeholder="Ask a follow-up question... (Ctrl+Enter to send)"
              value={followUpInput}
              onInput={(e: Event) => setFollowUpInput((e.target as HTMLTextAreaElement).value)}
              onKeyDown={handleKeyDown}
              rows={2}
              class="resize-none flex-1 !min-h-[40px]"
            />
            <Button
              size="icon"
              onClick={handleSendFollowUp}
              disabled={loading || !followUpInput.trim()}
              class="self-end"
            >
              {loading ? (
                <LoaderCircle className="h-4 w-4 animate-spin" />
              ) : (
                <ArrowUp className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  function renderHistoryMode() {
    return (
      <div class="h-full overflow-y-auto p-4">
          {historyLoading ? (
            <div class="flex items-center justify-center h-full">
              <LoaderCircle className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div class="flex flex-col items-center justify-center h-full text-muted-foreground">
              <AlertCircle className="h-12 w-12 mb-4 text-destructive opacity-50" />
              <p class="text-center text-destructive">{error}</p>
              <Button
                variant="outline"
                class="mt-4"
                onClick={() => loadHistory()}
              >
                Try Again
              </Button>
            </div>
          ) : history.length === 0 ? (
            <div class="flex flex-col items-center justify-center h-full text-muted-foreground">
              <History className="h-12 w-12 mb-4 opacity-50" />
              <p class="text-center">No previous bulk analyses found.</p>
              <p class="text-sm text-center mt-2">
                Select 2-5 deductions and click "Bulk AI" to start an analysis.
              </p>
            </div>
          ) : (
            <div class="space-y-3">
              <h3 class="font-medium text-sm text-muted-foreground">Previous Analyses</h3>
              {history.map((conv) => (
                <div
                  key={conv.id}
                  class="p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => handleViewHistoryItem(conv)}
                >
                  <div class="flex items-start justify-between gap-2">
                    <div class="flex-1 min-w-0">
                      <p class="text-sm font-medium line-clamp-2">{conv.initial_prompt}</p>
                      <p class="text-xs text-muted-foreground mt-1">
                        {conv.deduction_ids.length} deductions •{" "}
                        {format(parseISO(conv.created_at), "MMM d, yyyy HH:mm")}
                      </p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-1" />
                  </div>

                  {conv.results && (
                    <div class="flex gap-1 mt-2 flex-wrap">
                      {conv.results.deductions.map((d: BulkDeductionResult) => (
                        <Badge
                          key={d.deduction_id}
                          variant="outline"
                          class={cn(
                            "text-xs px-1.5 py-0",
                            d.is_disputable
                              ? "border-green-500/50 text-green-600"
                              : "border-red-500/50 text-red-600"
                          )}
                        >
                          {d.is_disputable ? (
                            <CheckCircle2 className="h-3 w-3 mr-0.5" />
                          ) : (
                            <XCircle className="h-3 w-3 mr-0.5" />
                          )}
                          {d.invoice_number}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
      </div>
    )
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" class="w-full sm:w-[550px] p-0 gap-0 flex flex-col h-full">
        <style>{`
          .markdown-content {
            line-height: 1.6;
          }
          .markdown-content h1,
          .markdown-content h2,
          .markdown-content h3,
          .markdown-content h4 {
            font-weight: 600;
            margin-top: 1em;
            margin-bottom: 0.5em;
          }
          .markdown-content h1 { font-size: 1.5em; }
          .markdown-content h2 { font-size: 1.3em; }
          .markdown-content h3 { font-size: 1.1em; }
          .markdown-content p {
            margin-bottom: 0.75em;
          }
          .markdown-content ul,
          .markdown-content ol {
            margin-left: 1.5em;
            margin-bottom: 0.75em;
          }
          .markdown-content li {
            margin-bottom: 0.25em;
          }
          .markdown-content strong {
            font-weight: 600;
          }
          .line-clamp-2 {
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
        `}</style>
        
        <SheetHeader class="p-4 border-b bg-indigo-50 shrink-0">
          <div class="flex items-center gap-3">
            {viewingHistoryItem && (
              <Button
                variant="ghost"
                size="icon"
                class="h-8 w-8 hover:bg-indigo-100"
                onClick={handleBackToHistory}
              >
                <ArrowLeft className="h-4 w-4 text-indigo-600" />
              </Button>
            )}
            <div class="h-9 w-9 rounded-lg bg-indigo-100 flex items-center justify-center">
              <Wand2 className="h-5 w-5 text-indigo-500" />
            </div>
            <div class="flex-1">
              <SheetTitle class="text-base font-semibold text-gray-900">
                Bulk Wizard
              </SheetTitle>
              <SheetDescription class="text-xs mt-0.5">
                {mode === "analysis" && conversation
                  ? `Analyzing ${conversation.deduction_ids.length} deductions`
                  : mode === "analysis" && selectedDeductions.length > 0
                  ? `${selectedDeductions.length} deductions selected`
                  : "View past analyses"}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div class="flex-1 min-h-0">
          {mode === "analysis" ? renderAnalysisMode() : renderHistoryMode()}
        </div>
      </SheetContent>

      {/* Warning dialog for already analyzed deductions */}
      <AlertDialog open={showWarningDialog} onOpenChange={setShowWarningDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle class="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-600" />
              Some Deductions Already Analyzed
            </AlertDialogTitle>
            <AlertDialogDescription class="pt-2">
              <p class="mb-3">
                The following {alreadyAnalyzedIds.length === 1 ? "deduction has" : "deductions have"} already been analyzed:
              </p>
              <div class="bg-muted p-3 rounded-lg mb-3 max-h-40 overflow-y-auto">
                <ul class="list-disc list-inside space-y-1 text-sm">
                  {alreadyAnalyzedIds.map((id: string) => {
                    const deduction = selectedDeductions.find((d: DeductionListJson) => d.id === id)
                    return (
                      <li key={id}>
                        {deduction ? deduction.invoice_number : id}
                      </li>
                    )
                  })}
                </ul>
              </div>
              <p>
                Running the bulk wizard again will create a new analysis, which may overwrite previous results. Do you want to continue?
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelWarning}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmWarning}>
              Continue Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  )
}
